import Text from "./text";

export default class Canvas extends Text {
    canvasWidth;
    canvasHeight;
    messageCanvas;
    messageCanvasContext;
    transparent;
    maxHeight;
    opacity;

    constructor(properties) {
        super(properties);
        this.canvasWidth = 0;
        this.canvasHeight = 0;
        let dimension = canvasMaxHeightWidthPercentageToPixel(properties.Camera);
        let defaultDimension = defaultMaxHeightWidthPercentageToPixel();
        this.maxWidth = dimension?.maxWidth || defaultDimension?.maxWidth || 0;
        this.maxHeight = dimension?.maxHeight || defaultDimension?.maxHeight || 0;
        this.messageCanvas = document.createElement('canvas');
        this.messageCanvasContext = this.messageCanvas.getContext('2d');
        this.transparent = properties.transparent || true;
        this.opacity = properties.opacity || 1;
    }

    updateCanvasSize(width, height) {
        this.canvasWidth = width || this.canvasWidth;
        this.canvasHeight = height || this.canvasHeight;
        this.messageCanvas.width = this.canvasWidth;
        this.messageCanvas.height = this.canvasHeight;
    }

    createVRMessage() {
        let newVRMessage = {};
        newVRMessage.object = this.makeTextSprite({
            renderOrder: RenderOrderEnum.VRMessageRO
        });

        return newVRMessage;
    }

    makeTextSprite(parameters = {}) {
        this.updateCanvasText(parameters);
        return this.getSpriteFromCanvas(parameters);
    }

    setCanvasFont() {
        this.messageCanvasContext.font = "Bold " + this.fontSize + "px " + this.fontFace;
    }

    setCanvasBackground() {
        this.messageCanvasContext.fillStyle = "rgba(" + this.backgroundColor.r + "," + this.backgroundColor.g + ","
            + this.backgroundColor.b + "," + this.backgroundColor.a + ")";
    }

    setCanvasBorder() {
        this.messageCanvasContext.strokeStyle = "rgba(" + this.borderColor.r + "," + this.borderColor.g + ","
            + this.borderColor.b + "," + this.borderColor.a + ")";
    }

    setCanvasTextColor() {
        this.messageCanvasContext.fillStyle = "rgba(" + this.textColor.r + "," + this.textColor.g + ","
            + this.textColor.b + "," + this.textColor.a + ")";
    }

    getMaxWidth() {
        return this.maxWidth ? this.maxWidth * 0.99 : .8 * window.innerWidth;
    }

    updateCanvasText() {
        // setting font first time to measure text width and height
        this.setCanvasFont();
        const metrics = this.computeTextMetrics();

        this.updateCanvasSize(metrics.textWidth, metrics.textHeight);
        this.applyCanvasStyles();
        this.drawTextContent(metrics);
    }

    applyCanvasStyles() {
        this.setCanvasFont();
        this.setCanvasBackground();
        this.setCanvasBorder();

        this.messageCanvasContext.lineWidth = this.borderThickness;

        straightRect(
            this.messageCanvasContext,
            this.borderThickness / 2,
            this.borderThickness / 2,
            this.messageCanvas.width - this.borderThickness / 2,
            this.messageCanvas.height - this.borderThickness / 2
        );

        this.setCanvasTextColor();
    }

    drawTextContent(metrics) {
        let startY = metrics.verticalPadding;
        const availableHeight = this.messageCanvas.height - (2 * this.borderThickness);
        if (metrics.baseTextHeight < availableHeight) {
            startY = (availableHeight - metrics.baseTextHeight) / 2 + this.borderThickness;
        }

        for (let i = 0; i < metrics.totalLines; i++) {
            let xPosition = this.borderThickness;
            if (this.textCenterAlign) {
                const lineWidth = this.messageCanvasContext.measureText(metrics.texts[i]).width;
                xPosition = (metrics.textWidth / 2) - (lineWidth / 2);
            }

            const yPosition = startY + (i * this.fontSize * metrics.lineSpacing) + this.fontSize;
            this.messageCanvasContext.fillText(metrics.texts[i], xPosition, yPosition);
        }
    }

    computeTextMetrics() {
        const normalizedText = Array.isArray(this.text) ? this.text.join(', ') : this.text;
        this.fontSize = this.configData?.fontSize || 24;
        this.setCanvasFont();

        const maxWidth = this.getMaxWidth();
        const maxLines = this.getMaxLinesAllowed();

        const lines = this.processTextIntoLines(normalizedText, maxWidth);
        const processedLines = this.handleOverflow(lines, normalizedText, maxWidth, maxLines);
        const paddedLines = processedLines.map(line => ` ${line} `);
        const metrics = this.calculateTextDimensions(paddedLines);

        return {
            texts: paddedLines,
            totalLines: paddedLines.length,
            textWidth: metrics.width,
            textHeight: metrics.height,
            lineSpacing: metrics.lineSpacing,
            verticalPadding: metrics.verticalPadding,
            baseTextHeight: metrics.baseTextHeight
        };
    }

    processTextIntoLines(text, maxWidth) {
        const paragraphs = text.split('\n');
        const resultLines = [];

        for (const paragraph of paragraphs) {
            if (!paragraph.trim()) {
                resultLines.push('');
                continue;
            }

            const words = paragraph.split(' ');
            let currentLine = '';

            for (const word of words) {
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                const metrics = this.messageCanvasContext.measureText(testLine);

                if (metrics.width <= maxWidth) {
                    currentLine = testLine;
                } else {
                    if (currentLine) {
                        resultLines.push(currentLine);
                        currentLine = word;
                    } else {
                        resultLines.push(word);
                    }
                }
            }

            if (currentLine) {
                resultLines.push(currentLine);
            }
        }

        return resultLines;
    }

    handleOverflow(lines, originalText, maxWidth, maxLines) {
        let processedLines = [...lines];
        const originalFontSize = this.fontSize;

        if (processedLines.length > maxLines) {
            while (processedLines.length > maxLines && this.fontSize > this.minFontSize) {
                this.fontSize--;
                this.setCanvasFont();
                processedLines = this.processTextIntoLines(originalText, maxWidth);
                maxLines = this.getMaxLinesAllowed();
            }

            if (this.fontSize === this.minFontSize && processedLines.length > maxLines) {
                processedLines = processedLines.slice(0, maxLines);
                let lastLine = processedLines[maxLines - 1];
                while (this.messageCanvasContext.measureText(`${lastLine}...`).width > maxWidth && lastLine.length > 0) {
                    lastLine = lastLine.substring(0, lastLine.length - 1);
                }

                processedLines[maxLines - 1] = lastLine.length > 0 ? `${lastLine.trim()}...` : '...';
            }
        }

        return processedLines;
    }

    calculateTextDimensions(lines) {
        let maxLineWidth = 0;
        for (const line of lines) {
            const metrics = this.messageCanvasContext.measureText(line);
            maxLineWidth = Math.max(maxLineWidth, metrics.width);
        }

        const horizontalPadding = this.borderThickness;
        const verticalPadding = this.borderThickness;
        const width = maxLineWidth + (2 * horizontalPadding);

        const lineSpacing = 1.3;
        const baseTextHeight = this.fontSize * lines.length * lineSpacing;
        const height = baseTextHeight + (2 * verticalPadding);

        return {
            width,
            height,
            lineSpacing,
            verticalPadding,
            baseTextHeight
        };
    }

    calcMaxCharPerLine(text) {
        let avgCharLength = (this.messageCanvasContext.measureText(text)).width / text.length;
        let allowedChar = Math.floor(this.getMaxWidth() / avgCharLength);

        if (allowedChar >= text.length) {
            return text.length + 1;
        }

        if (this.fontSize === this.minFontSize) {
            for (let i = allowedChar - 1; i >= 0; i--) {
                if (text[i] === ' ') {
                    allowedChar = i + 1; // Move one position after the space
                    break;
                }
            }

            return allowedChar;
        }

        this.decrementFontSize();
        this.setCanvasFont();
        return this.calcMaxCharPerLine(text);
    }

    getMaxLinesAllowed() {
        if (this.maxHeight) {
            const lineSpacingFactor = 1.3;
            const avHeight = this.maxHeight - (2 * this.borderThickness);
            const lineHeight = this.fontSize * lineSpacingFactor;
            return Math.floor(avHeight / lineHeight);
        } else {
            return wv.tooltipTextMaxline;
        }
    }


    adjustTextWidth(texts, pos) {
        if (texts.length <= this.getMaxLinesAllowed()) {
            const allowedChar = this.calcMaxCharPerLine(texts[pos]);
            const ellipsisCharLength = 4;

            if (texts.length >= this.getMaxLinesAllowed()) {
                let str = texts[pos].slice(0, allowedChar - ellipsisCharLength);
                if (allowedChar <= 4)
                    str = "";
                str.trim();
                str += "...";
                texts[pos] = str;
            } else {
                let str = texts[pos].slice(0, allowedChar - 1);
                str.trim();
                let resStr = texts[pos].slice(allowedChar - 1, texts[pos].length);
                texts[pos] = str;

                for (let i = texts.length; i > pos + 1; i--)
                    texts[i] = texts[i - 1];

                texts[pos + 1] = resStr;
            }

        }
    }

    getSpriteFromCanvas(parameters) {
        let texture = new THREE.Texture(this.messageCanvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;

        let spriteMaterial = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            side: THREE.DoubleSide
        });


        if (this.transparent === true) {
            spriteMaterial.transparent = true;
            spriteMaterial.depthWrite = false;
            spriteMaterial.depthTest = false;
        }
        let spriteGeometry = new THREE.PlaneGeometry(1, 1);

        let sprite = new THREE.Mesh(spriteGeometry, spriteMaterial);
        sprite.isVRMessage = true;
        sprite.scale_width = this.messageCanvas.width;
        sprite.scale_height = this.messageCanvas.height;
        sprite.renderOrder = parameters.renderOrder !== undefined ? parameters.renderOrder : RenderOrderEnum.tooltipRO;
        return sprite;
    }
}
